import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProject } from '../context/ProjectContext';
import { useToast } from '../hooks/useToast';
import { TaskStatus, Task, Role, Project } from '../types';
import { db } from '../services';
import { generateId } from '../utils/formatters';
import { sanitizeInput } from '../utils/validation';
import { ConfirmModal } from '../components/common';
import KanbanColumn from './components/KanbanColumn';
import ChatSection from './components/ChatSection';

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const { projects, tasks, timeline, users, setProjects, setTasks, addTimelineEvent, addNotification } = useProject();
  const { showSuccess, showError } = useToast();

  // Get initial tab from URL query param
  const initialTab = (searchParams.get('tab') as 'board' | 'chat' | 'timeline' | 'settings') || 'board';
  const [activeTab, setActiveTab] = useState<'board' | 'chat' | 'timeline' | 'settings'>(initialTab);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // Update tab when URL changes (e.g., from notification click)
  useEffect(() => {
    const tabParam = searchParams.get('tab') as 'board' | 'chat' | 'timeline' | 'settings';
    if (tabParam && ['board', 'chat', 'timeline', 'settings'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);
  
  const [newTask, setNewTask] = useState({ 
    title: '', 
    description: '', 
    assigneeId: '', 
    deadline: new Date().toISOString().split('T')[0], 
    priority: 'Medium' as Task['priority'] 
  });

  const project = projects.find(p => p.id === id);
  const [editProject, setEditProject] = useState<Project | null>(project || null);

  const projectTasks = useMemo(() => tasks.filter(t => t.projectId === id), [tasks, id]);
  const projectTimeline = useMemo(() => timeline.filter(e => e.projectId === id), [timeline, id]);
  
  const projectMembers = useMemo(() => {
    if (!project) return [];
    return users.filter(u => project.memberIds.includes(u.id));
  }, [users, project?.memberIds]);

  if (!project) {
    return (
      <div className="p-8 text-center bg-white rounded-2xl m-4 border border-slate-200 shadow-sm">
        Project not found.
      </div>
    );
  }

  const isArchived = project.status === 'archived';
  const isAuthorized = currentUser?.role === Role.ADMIN || 
    (currentUser?.role === Role.MANAGER && currentUser?.id === project.ownerId);

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAuthorized || isArchived || isSyncing) return;

    console.log('[ProjectDetail] Starting task creation...');
    setIsSyncing(true);

    const task: Task = {
      id: generateId(),
      projectId: id!,
      title: sanitizeInput(newTask.title),
      description: sanitizeInput(newTask.description),
      status: TaskStatus.TODO,
      assigneeId: newTask.assigneeId || currentUser!.id,
      deadline: newTask.deadline,
      estimateHours: Math.floor(Math.random() * 8) + 2,
      priority: newTask.priority,
      checklist: []
    };

    try {
      console.log('[ProjectDetail] Creating task in database:', task.id, task.title);
      await db.createTask(task);
      console.log('[ProjectDetail] Task created successfully in database');

      setTasks(prev => [...prev, task]);
      console.log('[ProjectDetail] Task added to local state');

      // Non-critical operations
      try {
        console.log('[ProjectDetail] Adding timeline event...');
        await addTimelineEvent(id!, 'task_created', `New task: ${task.title}`);
        console.log('[ProjectDetail] Timeline event added');
      } catch (e) {
        console.warn('[ProjectDetail] Timeline event failed:', e);
      }

      showSuccess('🚀 Task created!');

      setShowTaskModal(false);
      setNewTask({ title: '', description: '', assigneeId: '', deadline: new Date().toISOString().split('T')[0], priority: 'Medium' });

      // Notify assignee with push notification
      try {
        console.log('[ProjectDetail] Sending notification to assignee...');
        const assigneeName = users.find(u => u.id === task.assigneeId)?.name || 'Someone';
        const isAssignedToSelf = task.assigneeId === currentUser?.id;

        await addNotification(
          '📝 Task Assigned',
          `You've been assigned: "${task.title}" in ${project.name}`,
          task.assigneeId,
          `/projects/${id}`,
          !isAssignedToSelf // Send push only if assigned to someone else
        );
        console.log('[ProjectDetail] Notification sent');
      } catch (e) {
        console.warn('[ProjectDetail] Notification failed:', e);
      }
    } catch (err: any) {
      console.error('[ProjectDetail] Task creation failed:', err);
      console.error('[ProjectDetail] Error details:', {
        message: err.message,
        code: err.code,
        details: err.details,
        hint: err.hint
      });
      showError(`Failed to create task: ${err.message || 'Unknown error'}`);
    } finally {
      console.log('[ProjectDetail] Task creation finished, resetting isSyncing');
      setIsSyncing(false);
    }
  };

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editProject || !isAuthorized) return;
    setIsSyncing(true);
    try {
      // Find newly added members
      const newMembers = editProject.memberIds.filter(id => !project.memberIds.includes(id));
      const removedMembers = project.memberIds.filter(id => !editProject.memberIds.includes(id));
      
      await db.updateProject(id!, editProject);
      setProjects(prev => prev.map(p => p.id === id ? editProject : p));
      await addTimelineEvent(id!, 'project_updated', 'Updated project details.');
      showSuccess('Settings saved to cloud.');
      
      // Notify newly added members with push notification
      for (const memberId of newMembers) {
        if (memberId !== currentUser?.id) {
          await addNotification(
            '📋 Added to Project',
            `You've been added to project "${editProject.name}"`,
            memberId,
            `/projects/${id}`,
            true // Send push
          );
        }
      }
      
      // Notify removed members (no push, just in-app)
      for (const memberId of removedMembers) {
        if (memberId !== currentUser?.id) {
          await addNotification(
            'Project Update',
            `You've been removed from project "${project.name}"`,
            memberId,
            undefined,
            false // No push for removal
          );
        }
      }
      
      // Notify existing members about updates (no push, just in-app)
      const existingMembers = editProject.memberIds.filter(id => 
        project.memberIds.includes(id) && id !== currentUser?.id
      );
      for (const memberId of existingMembers) {
        await addNotification(
          'Project Updated',
          `The details for ${editProject.name} have been updated.`,
          memberId,
          `/projects/${id}`,
          false // No push for updates
        );
      }
    } catch (err: any) {
      showError(`Cloud Error: ${err.message}`);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleArchiveProject = async () => {
    if (!editProject || !isAuthorized) return;
    const newStatus = editProject.status === 'archived' ? 'active' : 'archived';
    const updated = { ...editProject, status: newStatus as any };
    setIsSyncing(true);
    try {
      await db.updateProject(id!, updated);
      setProjects(prev => prev.map(p => p.id === id ? updated : p));
      await addTimelineEvent(id!, 'project_updated', `Project ${newStatus === 'archived' ? 'archived' : 'restored'}.`);
      setEditProject(updated);
      showSuccess(`Project ${newStatus === 'archived' ? 'Archived' : 'Restored'}`);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!isAuthorized) return;
    setIsSyncing(true);
    try {
      await db.deleteProject(id!);
      setProjects(prev => prev.filter(p => p.id !== id));
      navigate('/projects');
    } catch (err) {
      console.error(err);
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className="animate-in fade-in duration-500 max-w-full overflow-x-hidden p-3 md:p-8">
      {/* Sticky Header */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-lg -mx-3 -mt-3 px-3 pt-2 md:-mx-8 md:-mt-8 md:px-8 md:pt-6 pb-0 border-b border-slate-100 shadow-sm transition-all">
        {isArchived && (
          <div className="bg-rose-50 border border-rose-100 p-2 md:p-3 rounded-xl flex items-center justify-between shadow-sm mb-2 text-[10px] md:text-xs">
            <p className="font-black text-rose-900 uppercase tracking-widest">Read-only archived mode</p>
            {isAuthorized && (
              <button onClick={handleArchiveProject} className="font-black uppercase text-indigo-600 hover:underline px-2 py-1">Restore</button>
            )}
          </div>
        )}

        <header className="flex flex-col md:flex-row md:items-end justify-between gap-3 pb-2 md:pb-4">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 mb-0.5">
              <button onClick={() => navigate('/projects')} className="text-slate-400 hover:text-indigo-600 transition-colors p-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18"/></svg>
              </button>
              <span className="text-slate-400 text-[8px] md:text-[10px] font-black uppercase tracking-widest truncate">Workspace / {project.name}</span>
            </div>
            <h1 className="text-lg md:text-3xl font-black text-slate-900 tracking-tight truncate leading-tight">{project.name}</h1>
          </div>
          <div className="flex items-center gap-2 shrink-0 md:mb-1">
            {isAuthorized && !isArchived && (
              <button 
                onClick={() => setShowTaskModal(true)}
                className="flex-1 md:flex-none px-4 py-2 bg-indigo-600 text-white font-black text-[10px] md:text-xs uppercase tracking-widest rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-500/10 active:scale-95"
              >
                + New Task
              </button>
            )}
          </div>
        </header>

        <nav className="flex items-center gap-4 md:gap-6 overflow-x-auto no-scrollbar">
          {(['board', 'chat', 'timeline', 'settings'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2.5 md:py-3 px-1 text-[9px] md:text-[11px] font-black uppercase tracking-widest transition-all relative whitespace-nowrap ${
                activeTab === tab ? 'text-indigo-600' : 'text-slate-400 hover:text-slate-800'
              }`}
            >
              {tab}
              {activeTab === tab && <div className="absolute bottom-0 left-0 w-full h-0.5 md:h-1 bg-indigo-600 rounded-t-full" />}
            </button>
          ))}
        </nav>
      </div>

      <div className="mt-4 md:mt-8">
        {activeTab === 'board' && (
          <div className="flex flex-col lg:flex-row gap-4 md:gap-6 items-start pb-20">
            <div className="w-full lg:flex-1 min-w-0">
              <KanbanColumn 
                title="To Do" 
                status={TaskStatus.TODO} 
                tasks={projectTasks.filter(t => t.status === TaskStatus.TODO)} 
                onAddTask={isAuthorized && !isArchived ? () => setShowTaskModal(true) : undefined}
              />
            </div>
            <div className="w-full lg:flex-1 min-w-0">
              <KanbanColumn 
                title="In Progress" 
                status={TaskStatus.IN_PROGRESS} 
                tasks={projectTasks.filter(t => t.status === TaskStatus.IN_PROGRESS)} 
              />
            </div>
            <div className="w-full lg:flex-1 min-w-0">
              <KanbanColumn 
                title="Done" 
                status={TaskStatus.DONE} 
                tasks={projectTasks.filter(t => t.status === TaskStatus.DONE)} 
              />
            </div>
          </div>
        )}
        
        {activeTab === 'chat' && (
          <div className="h-[calc(100vh-14rem)] md:h-[calc(100vh-18rem)] min-h-[400px] mb-8">
            <ChatSection projectId={project.id} />
          </div>
        )}

        {activeTab === 'timeline' && (
          <div className="py-4 md:py-6 max-w-3xl mx-auto pb-20">
            <div className="space-y-3 px-1">
              {projectTimeline.length === 0 ? (
                <div className="py-20 text-center text-slate-400">
                  <p className="font-bold uppercase tracking-widest text-[9px]">No activity</p>
                </div>
              ) : (
                projectTimeline.map((ev) => {
                  const evUser = users.find(u => u.id === ev.userId);
                  return (
                    <div key={ev.id} className="flex gap-3 bg-white p-3 rounded-2xl border border-slate-100 shadow-sm items-center">
                      <img src={evUser?.avatar} className="w-8 h-8 rounded-lg object-cover" alt="" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-slate-900 truncate">{evUser?.name}</p>
                        <p className="text-[10px] text-slate-500 font-medium truncate">{ev.details}</p>
                      </div>
                      <span className="text-[9px] text-slate-400 font-black uppercase whitespace-nowrap">
                        {new Date(ev.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="max-w-3xl mx-auto pb-32 md:pb-20 space-y-6 md:space-y-8">
            <div className="p-6 md:p-12 bg-white rounded-[2rem] md:rounded-[2.5rem] border border-slate-100 shadow-sm">
              <h2 className="text-lg md:text-xl font-black text-slate-900 mb-6 md:mb-8 tracking-tight">Configuration</h2>
              <div className="space-y-4 md:space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-[9px] md:text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Name</label>
                    <input type="text" value={editProject?.name || ''} onChange={(e) => setEditProject(prev => prev ? {...prev, name: e.target.value} : null)} readOnly={!isAuthorized} className="w-full px-4 py-3 md:px-5 md:py-4 rounded-2xl border border-slate-200 bg-slate-50/50 outline-none font-bold text-slate-900 focus:bg-white" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-[9px] md:text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Overview</label>
                    <textarea value={editProject?.description || ''} onChange={(e) => setEditProject(prev => prev ? {...prev, description: e.target.value} : null)} readOnly={!isAuthorized} className="w-full px-4 py-3 md:px-5 md:py-4 rounded-2xl border border-slate-200 bg-slate-50/50 outline-none font-medium text-slate-700 h-28 resize-none focus:bg-white" />
                  </div>
                  <div>
                    <label className="block text-[9px] md:text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Lead</label>
                    <select value={editProject?.ownerId || ''} onChange={(e) => setEditProject(prev => prev ? {...prev, ownerId: e.target.value} : null)} disabled={!isAuthorized} className="w-full px-4 py-3 md:px-5 md:py-4 rounded-2xl border border-slate-200 bg-slate-50 font-bold outline-none disabled:opacity-50">
                      {(editProject?.memberIds || []).map(memberId => {
                        const member = users.find(u => u.id === memberId);
                        return member ? <option key={member.id} value={member.id}>{member.name}</option> : null;
                      })}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[9px] md:text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Deadline</label>
                    <input type="date" value={editProject?.deadline || ''} onChange={(e) => setEditProject(prev => prev ? {...prev, deadline: e.target.value} : null)} readOnly={!isAuthorized} className="w-full px-4 py-3 md:px-5 md:py-4 rounded-2xl border border-slate-200 bg-slate-50 outline-none font-bold text-slate-900" />
                  </div>
                </div>
              </div>
            </div>

            {/* Team Members Section */}
            <div className="p-6 md:p-12 bg-white rounded-[2rem] md:rounded-[2.5rem] border border-slate-100 shadow-sm">
              <h2 className="text-lg md:text-xl font-black text-slate-900 mb-6 md:mb-8 tracking-tight">Team Members</h2>

              {/* Current Members */}
              <div className="space-y-3 mb-6">
                {(editProject?.memberIds || []).map(memberId => {
                  const member = users.find(u => u.id === memberId);
                  if (!member) return null;
                  const isOwner = memberId === editProject?.ownerId;
                  return (
                    <div key={memberId} className="flex items-center gap-3 p-3 bg-slate-50 rounded-2xl">
                      <img src={member.avatar} alt={member.name} className="w-10 h-10 rounded-xl object-cover" />
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-slate-900 text-sm truncate">{member.name}</p>
                        <p className="text-[10px] text-slate-500 font-medium">{member.email}</p>
                      </div>
                      {isOwner && (
                        <span className="px-3 py-1 bg-indigo-100 text-indigo-700 text-[9px] font-black uppercase tracking-widest rounded-full">
                          Lead
                        </span>
                      )}
                      {isAuthorized && !isOwner && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditProject(prev => prev ? {
                              ...prev,
                              memberIds: prev.memberIds.filter(id => id !== memberId)
                            } : null);
                          }}
                          className="p-2 text-slate-400 hover:text-rose-600 transition-colors"
                          title="Remove member"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Add Members */}
              {isAuthorized && (
                <div>
                  <label className="block text-[9px] md:text-[11px] font-black uppercase tracking-widest text-slate-400 mb-3 ml-1">
                    Add Team Members
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-60 overflow-y-auto p-1">
                    {users
                      .filter(u => !(editProject?.memberIds || []).includes(u.id))
                      .map(user => (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => {
                            setEditProject(prev => prev ? {
                              ...prev,
                              memberIds: [...prev.memberIds, user.id]
                            } : null);
                          }}
                          className="flex items-center gap-3 p-3 bg-slate-50 hover:bg-indigo-50 rounded-xl transition-colors text-left group"
                        >
                          <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-lg object-cover" />
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-slate-700 text-sm truncate group-hover:text-indigo-700">{user.name}</p>
                            <p className="text-[10px] text-slate-400 font-medium truncate">{user.role}</p>
                          </div>
                          <svg className="w-5 h-5 text-slate-300 group-hover:text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                        </button>
                      ))}
                    {users.filter(u => !(editProject?.memberIds || []).includes(u.id)).length === 0 && (
                      <p className="col-span-2 text-center py-4 text-slate-400 text-sm">All users are already members</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Save Button - Fixed at bottom on mobile, static on desktop */}
            {isAuthorized && (
              <div className="fixed bottom-0 left-0 right-0 md:static p-4 md:p-0 bg-white md:bg-transparent border-t md:border-0 border-slate-200 shadow-lg md:shadow-none z-30">
                <button
                  onClick={handleSaveSettings}
                  disabled={isSyncing}
                  className="w-full md:w-auto px-10 py-4 bg-indigo-600 text-white font-black uppercase tracking-widest text-[10px] md:text-xs rounded-2xl shadow-xl shadow-indigo-600/20 active:scale-95 disabled:opacity-50 transition-all"
                >
                  {isSyncing ? 'Processing...' : 'Save All Changes'}
                </button>
              </div>
            )}


            {isAuthorized && (
              <div className="p-6 md:p-12 bg-rose-50/30 rounded-[2rem] md:rounded-[2.5rem] border border-rose-100 shadow-sm">
                <h3 className="text-lg font-black text-rose-900 mb-6 tracking-tight">Danger Zone</h3>
                <div className="flex flex-col md:flex-row gap-3">
                  <button onClick={handleArchiveProject} className="flex-1 px-8 py-3.5 bg-white text-rose-600 border border-rose-200 font-black uppercase tracking-widest text-[10px] rounded-2xl hover:bg-rose-50">
                    {project.status === 'archived' ? 'Restore Project' : 'Archive Project'}
                  </button>
                  <button onClick={() => setShowDeleteModal(true)} className="flex-1 px-8 py-3.5 bg-rose-600 text-white font-black uppercase tracking-widest text-[10px] rounded-2xl hover:bg-rose-700 shadow-xl shadow-rose-600/20">
                    Delete Forever
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteProject}
        title="Are you sure?"
        message={`Permanently delete "${project.name}" and all cloud records.`}
        confirmText="Delete"
        isLoading={isSyncing}
        variant="danger"
      />

      {/* New Task Modal */}
      {showTaskModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white w-full max-w-xl rounded-[2rem] md:rounded-[2.5rem] shadow-2xl p-6 md:p-10 animate-in zoom-in-95 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-black text-slate-900 tracking-tight">New Task</h2>
              <button onClick={() => setShowTaskModal(false)} className="p-2 text-slate-400 hover:text-rose-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            
            <form onSubmit={handleAddTask} className="space-y-6">
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Title</label>
                <input type="text" value={newTask.title} onChange={(e) => setNewTask({...newTask, title: e.target.value})} required className="w-full px-5 py-3 md:py-4 rounded-2xl border border-slate-200 bg-slate-50 outline-none font-bold focus:bg-white focus:border-indigo-500" />
              </div>

              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Description</label>
                <textarea value={newTask.description} onChange={(e) => setNewTask({...newTask, description: e.target.value})} className="w-full px-5 py-3 md:py-4 rounded-2xl border border-slate-200 bg-slate-50 outline-none font-medium h-24 resize-none focus:bg-white focus:border-indigo-500" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Assignee</label>
                  <select value={newTask.assigneeId} onChange={(e) => setNewTask({...newTask, assigneeId: e.target.value})} required className="w-full px-5 py-3 md:py-4 rounded-2xl border border-slate-200 bg-slate-50 font-bold outline-none">
                    <option value="">Select</option>
                    {projectMembers.map(m => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Deadline</label>
                  <input type="date" value={newTask.deadline} onChange={(e) => setNewTask({...newTask, deadline: e.target.value})} required className="w-full px-5 py-3 md:py-4 rounded-2xl border border-slate-200 bg-slate-50 font-bold outline-none" />
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button type="button" onClick={() => setShowTaskModal(false)} className="flex-1 py-4 bg-slate-100 text-slate-600 font-black uppercase tracking-widest text-[10px] rounded-2xl hover:bg-slate-200">Cancel</button>
                <button type="submit" disabled={isSyncing} className="flex-1 py-4 bg-indigo-600 text-white font-black uppercase tracking-widest text-[10px] rounded-2xl shadow-xl shadow-indigo-500/20 active:scale-95 transition-all disabled:opacity-50">
                  {isSyncing ? 'Creating...' : 'Create Task'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetail;
